'use client';

import React, { useEffect, useState } from 'react';
import { BlockRenderer } from '@/core/components/Builder/BlockRenderer';
import { getPublicMediaUrl } from '@/core/utils/media';
import { useHeaderAtTransparentTop, useHeaderTransparency } from '@/core/components/providers/HeaderTransparencyContext';
import { cn } from '@/core/utils/cn';
import type { PublishedLayoutSnapshot } from './published-layout';

type StorefrontViewport = 'desktop' | 'tablet' | 'mobile';

export function StorefrontChromeLayout({
  kind,
  snapshot,
  initialViewport,
}: {
  kind: 'header' | 'footer';
  snapshot: PublishedLayoutSnapshot;
  initialViewport: StorefrontViewport;
}) {
  const globals = snapshot.content.find((block: any) => block?.type === 'globals')?.settings || {};
  const blocks = snapshot.content.filter((block: any) => block?.type !== 'globals');
  const Element = kind;
  const stickyMode = kind === 'header'
    ? (globals.stickyMode || (globals.sticky ? 'always' : 'none'))
    : 'none';
  const [scrollHidden, setScrollHidden] = useState(false);
  const transparency = useHeaderTransparency();
  const transparentAtTop = useHeaderAtTransparentTop();
  const transparentMode = kind === 'header' && transparency.enabled;

  useEffect(() => {
    if (stickyMode !== 'scroll-up') {
      setScrollHidden(false);
      return;
    }
    let previousY = window.scrollY;
    const onScroll = () => {
      const currentY = window.scrollY;
      const movingDown = currentY > previousY && currentY > 96;
      setScrollHidden(movingDown);
      previousY = currentY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [stickyMode]);

  // Over a transparent hero the whole chrome must be see-through, not just <header> itself:
  // a background set on a top-level row/section (the usual way to colour a header) would
  // otherwise paint a solid band over the hero and hide the light text/logo. Once scrolled
  // past the hero, data-transparent-top flips to false and the row backgrounds come back.
  const transparentTopCss = kind === 'header'
    ? `[data-layout-kind="header"][data-layout-version="${snapshot.version}"][data-transparent-top="true"] > div > .block-wrapper { background-color: transparent !important; background-image: none !important; box-shadow: none !important; border-color: transparent !important; }`
    : '';
  const visibilityCss = `${transparentTopCss}
[data-layout-kind="${kind}"][data-layout-version="${snapshot.version}"] { display: block; }
@media (max-width: 767px) { [data-layout-kind="${kind}"][data-layout-version="${snapshot.version}"] { ${globals.hideMobile ? 'display: none !important;' : ''} } }
@media (min-width: 768px) and (max-width: 1023px) { [data-layout-kind="${kind}"][data-layout-version="${snapshot.version}"] { ${globals.hideTablet ? 'display: none !important;' : ''} } }
@media (min-width: 1024px) { [data-layout-kind="${kind}"][data-layout-version="${snapshot.version}"] { ${globals.hideDesktop ? 'display: none !important;' : ''} } }`;
  return (
    <Element
      data-layout-builder="v2"
      data-layout-kind={kind}
      data-layout-version={snapshot.version}
      data-transparent-hero={transparentMode ? 'true' : 'false'}
      data-transparent-top={transparentAtTop ? 'true' : 'false'}
      className={cn(transparentAtTop && 'storefront-chrome-transparent-top')}
      style={{
        position: transparentMode ? 'fixed' : stickyMode !== 'none' ? 'sticky' : undefined,
        top: transparentMode
          ? `calc(var(--alert-bar-h, 0px) + ${Number(globals.stickyOffset || 0)}px)`
          : stickyMode !== 'none' ? Number(globals.stickyOffset || 0) : undefined,
        left: transparentMode ? 0 : undefined,
        right: transparentMode ? 0 : undefined,
        width: transparentMode ? '100%' : undefined,
        backgroundColor: transparentAtTop ? 'transparent' : globals.pageBackground || (transparentMode ? '#ffffff' : undefined),
        color: transparentAtTop ? transparency.textColor : globals.pageTextColor || (transparentMode ? '#0f172a' : undefined),
        backgroundImage: !transparentAtTop && globals.pageBackgroundImage ? `url(${getPublicMediaUrl(globals.pageBackgroundImage)})` : undefined,
        backgroundSize: globals.pageBackgroundSize || undefined,
        zIndex: globals.zIndex ?? (kind === 'header' ? 50 : undefined),
        backdropFilter: !transparentAtTop && globals.stickyBackdropBlur ? 'blur(14px)' : undefined,
        WebkitBackdropFilter: !transparentAtTop && globals.stickyBackdropBlur ? 'blur(14px)' : undefined,
        boxShadow: !transparentAtTop && globals.stickyShadow ? '0 10px 30px rgba(15, 23, 42, 0.10)' : undefined,
        transform: scrollHidden ? 'translateY(calc(-100% - 16px))' : 'translateY(0)',
        transition: 'transform 220ms ease, background-color 220ms ease, color 220ms ease, box-shadow 220ms ease, backdrop-filter 220ms ease',
      }}
    >
      <style>{visibilityCss}</style>
      <BlockRenderer blocks={blocks} viewport={initialViewport} />
    </Element>
  );
}

interface BoundaryProps { fallback: React.ReactNode; children: React.ReactNode }
interface BoundaryState { failed: boolean }

export class StorefrontChromeBoundary extends React.Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { failed: false };
  static getDerivedStateFromError(): BoundaryState { return { failed: true }; }
  componentDidCatch(error: unknown) { console.error('Layout builder v2 render failed; using legacy layout.', error); }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

/** Render the published builder during SSR; legacy is only an error fallback. */
export function SafeStorefrontChrome({
  kind,
  snapshot,
  fallback,
  initialViewport,
}: {
  kind: 'header' | 'footer';
  snapshot: PublishedLayoutSnapshot;
  fallback: React.ReactNode;
  initialViewport: StorefrontViewport;
}) {
  return (
    <StorefrontChromeBoundary fallback={fallback}>
      <StorefrontChromeLayout kind={kind} snapshot={snapshot} initialViewport={initialViewport} />
    </StorefrontChromeBoundary>
  );
}
